import axios from 'axios';

import { extractData, logError } from '@/api/ApiHelpers';

import type { ApiPayload } from '@/api/ApiTypes';


export type DatabaseIndexPresenceFilter = 'present' | 'deleted';

export type DatabaseIndexCheckLevel = 'fast' | 'extended' | 'deep';

export type DatabaseIndexesQueryParams = {
	page?: number;
	page_size?: number;
	sort?: string;
	sort_dir?: 'asc' | 'desc';
	presence?: DatabaseIndexPresenceFilter;
	database_alias?: string;
};

export type IndexHealthLevelCheck = {
	status: string;
	display_status: string;
	started_at: string | null;
	duration_ms: number | null;
	error: string | null;
};

export type IndexHealthMonitoredIndexItem = {
	id: number;
	database_alias: string;
	schema_name: string;
	table_name: string;
	index_name: string;
	index_type: string;
	relation_kind: string;
	size_bytes: number;
	idx_scan: number | null;
	index_stats: {
		avg_leaf_density?: number | null;
		leaf_fragmentation?: number | null;
	};
	stats_collected_at: string | null;
	is_valid: boolean;
	is_ready: boolean;
	is_live: boolean;
	is_unique: boolean;
	is_primary: boolean;
	is_present: boolean;
	check_eligible: boolean;
	generation: number;
	fast: IndexHealthLevelCheck;
	extended: IndexHealthLevelCheck;
	deep: IndexHealthLevelCheck;
	unresolved_integrity_error_at: string | null;
	unresolved_integrity_error_message: string | null;
};

export type DatabaseIndexesResponse = ApiPayload & {
	items: IndexHealthMonitoredIndexItem[];
	count: number;
	page: number;
	page_size: number;
	summary: Record<string, unknown>;
	default_check_level: DatabaseIndexCheckLevel;
};

export type DatabaseIndexDefaultLevelResponse = ApiPayload & {
	default_check_level: DatabaseIndexCheckLevel;
};

export type DatabaseIndexResetAllChecksResponse = ApiPayload & {
	reset_count: number;
};

const utilsDatabaseIndexesGet = (
	params: DatabaseIndexesQueryParams,
	handler: (responseData: DatabaseIndexesResponse) => void,
): void => {
	axios.get('/utils/database_indexes/', {
		params,
	}).then(extractData(handler)).catch(logError);
};

const utilsDatabaseIndexDefaultLevelSet = (
	level: DatabaseIndexCheckLevel,
	handler: (responseData: DatabaseIndexDefaultLevelResponse) => void,
): void => {
	axios.post('/utils/database_indexes/default_level/', { level }).then(extractData(handler)).catch(logError);
};

const utilsDatabaseIndexChecksReset = (
	indexId: number,
	handler: (responseData: ApiPayload) => void,
): void => {
	axios.post('/utils/database_indexes/reset_checks/', { index_id: indexId }).then(extractData(handler)).catch(logError);
};

const utilsDatabaseIndexChecksResetAll = (
	handler: (responseData: DatabaseIndexResetAllChecksResponse) => void,
): void => {
	axios.post('/utils/database_indexes/reset_all_checks/', {}).then(extractData(handler)).catch(logError);
};

export default {
	utilsDatabaseIndexesGet,
	utilsDatabaseIndexDefaultLevelSet,
	utilsDatabaseIndexChecksReset,
	utilsDatabaseIndexChecksResetAll,
};
